import * as React from "react";
import { useState, useCallback } from "react";
import styled, { css } from "styled-components";
import { useQueryClient } from "@tanstack/react-query";
import { COLLECTIONS_QUERY_KEY } from "collections/hooks/useCollections";
import { BootstrapModalConfirm, MenuItem, DeleteMenuItem } from "components/bootstrap";
import { useDeleteEventProcedure } from "security-app/hooks/useEventProceduresAPI";
import { Icon, IfPermitted, HasOwnership } from "components/common";
import { useModalContext } from "common/contexts";
import EntityShareModal from "components/permissions/EntityShareModal";
import usePluggableEntitySharedActions from "hooks/usePluggableEntitySharedActions";
import useGetPermissionsByScope from "hooks/useScopePermissions";
import { MoreActionsMenu } from "components/common/MoreActions";
const Wrapper = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: ${theme.spacings.xs};
  `
);
function RowActionMenu({ eventProcedure }) {
  const { setModal, setEntity } = useModalContext();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEntityShareModal, setShowEntityShareModal] = useState(false);
  const queryClient = useQueryClient();
  const { deleteEventProcedure } = useDeleteEventProcedure();
  const { scopePermissions } = useGetPermissionsByScope(eventProcedure);
  const isMutable = !!(scopePermissions == null ? void 0 : scopePermissions.is_mutable);
  const { actions: pluggableActions, actionModals: pluggableActionModals } = usePluggableEntitySharedActions(eventProcedure, "event_procedure", () => queryClient.invalidateQueries({ queryKey: [COLLECTIONS_QUERY_KEY] }));
  const moreActions = [pluggableActions.length ? pluggableActions : null].filter(Boolean);
  const onEdit = () => {
    setEntity(eventProcedure.id);
    setModal("EDIT-EVENT-PROCEDURE");
  };
  const onDelete = async () => {
    await deleteEventProcedure({ id: eventProcedure.id });
    setShowDeleteModal(false);
  };
  const toggleEntityShareModal = useCallback(() => {
    setShowEntityShareModal((cur) => !cur);
  }, []);
  return /* @__PURE__ */ React.createElement(Wrapper, null, /* @__PURE__ */ React.createElement(MoreActionsMenu, { title: "Toggle event procedure actions" }, isMutable && /* @__PURE__ */ React.createElement(IfPermitted, { permissions: [`event_procedures:edit:${eventProcedure.id}`] }, /* @__PURE__ */ React.createElement(MenuItem, { onClick: onEdit }, /* @__PURE__ */ React.createElement(Icon, { name: "edit_square", size: "xs" }), " Edit")), /* @__PURE__ */ React.createElement(HasOwnership, { id: eventProcedure.id, type: "event_procedure" }, ({ disabled: hasMissingPermissions }) => /* @__PURE__ */ React.createElement(MenuItem, { disabled: hasMissingPermissions, onClick: toggleEntityShareModal }, /* @__PURE__ */ React.createElement(Icon, { name: "person_add", size: "xs" }), " Share")), moreActions, isMutable && /* @__PURE__ */ React.createElement(IfPermitted, { permissions: [`event_procedures:delete:${eventProcedure.id}`] }, /* @__PURE__ */ React.createElement(DeleteMenuItem, { onClick: () => setShowDeleteModal(true) }, /* @__PURE__ */ React.createElement(Icon, { name: "delete", size: "xs" }), " Delete"))), /* @__PURE__ */ React.createElement(
    BootstrapModalConfirm,
    {
      title: "Are you sure you want to delete this event procedure?",
      onConfirm: onDelete,
      onCancel: () => setShowDeleteModal(false),
      showModal: showDeleteModal
    },
    /* @__PURE__ */ React.createElement("div", null, "You are about to delete this event procedure: ", eventProcedure.title)
  ), showEntityShareModal && /* @__PURE__ */ React.createElement(
    EntityShareModal,
    {
      entityId: eventProcedure.id,
      entityType: "event_procedure",
      entityTypeTitle: "Event Procedure",
      entityTitle: eventProcedure.title,
      showShareableEntityURL: false,
      description: "Search for a User or Team to add as collaborator on this event procedure.",
      onClose: toggleEntityShareModal
    }
  ), pluggableActionModals);
}
export default RowActionMenu;
